import _ from 'lodash';
import axios from 'axios';
import audioHelper from './audio-helper';
import postEffects from './postEffects';
import wapiPluginsList from './plugins/plugins-list';
import { uuid } from 'app-core/Data';
import auth from 'auth';
import { createLogarithmicBuffer } from 'app-core/audio';
import { initSoundTouchNode } from './plugins/soundTouchNode.js';

class AudioSystem {
	isPlaying = false;
	trackEffectsPromises = null;
	trackSources = null;
	trackSourcesData = null;
	tracksBuffers = null;
	tracks = null;
	aggregatedTracks = null;
	isTrial = null;
	opts = null;
	audioContext = null;
	masterTrackConfig = null;
	soloSubTrackIds = null;
	soloTrackId = null;

	initOpts = null;

	listeners = [];

	states = {
		stopping: false,
		initPlaying: false
	}

	constructor(opts) {
		this.init(opts);
	}

	processedResolvers = [];

	async processed() {
		if (!this.isBusy) return true;
		return new Promise(res => {
			this.processedResolvers.push(res);
		});
	}

	isBusy = 0;
	setIsBusy(v) {
		this.isBusy += v ? 1 : -1;

		if (this.isBusy === 0) {
			this.processedResolvers.map(f => f(true));
			this.processedResolvers = [];
		}
	}

	init(opts) {
		this.audioContext = opts?.audioContext || this.audioContext;
		this.opts = opts || {};
		this.isTrial = opts?.isTrial;
		this.trackSources = opts?.trackSources || {};
		this.trackSourcesData = opts?.trackSourcesData || {};
		this.tracksBuffers = opts?.tracksBuffers || {};
		this.trackEffectsPromises = opts?.trackEffectsPromises || {};
		this.isPlaying = opts?.isPlaying || false;
		this.tracks = opts?.tracks || [];
		this.masterTrackConfig = opts?.masterTrackConfig;
		this.aggregatedTracks = opts?.aggregatedTracks;
		this.soloSubTrackIds = opts?.soloSubTrackIds;
		this.soloTrackId = opts?.soloTrackId;
		this.initOpts = opts || {};
		
		this.trackSourcesState = {};
		this.tracksEffects = {};
	}

	addEventListener(type, listener) {
		this.listeners.push({ type, listener });
	}

	removeEventListener(type, listener) { 
		this.listeners.splice(this.listeners.findIndex(l => l.listener === listener && l.type === type));
	}

	clearListeners() {
		this.listeners = [];
	}

	async checkWatermark(audioContext) {
		if (!this.isTrial) return;
		if (__DEBUG__)
			console.debug('Check watermark'/*, { audioContext } */);
		let buffer = await this.getWatermarkAudio();
		let source = audioContext.createBufferSource();
		source.loop = true;
		source.buffer = buffer;
		source.connect(audioContext.destination);
		source.start(audioContext.currentTime);
		this.watermarkPlayingSource = source;
		return source;
	}

	async getWatermarkAudio() {
		if (this.watermarkAudioBufferPromise) return this.watermarkAudioBufferPromise;

		if (__DEBUG__)
			console.debug('Get watermark');

		this.watermarkAudioBufferPromise = new Promise(res => {
			axios.get(require('@/assets/adthos_audio_watermark.mp3'), {
				responseType: 'blob'
			}).then(response => {
				let fr = new FileReader();
				fr.readAsArrayBuffer(response.data);
				fr.onloadend = (evt) => {
					return this.audioContext.decodeAudioData(evt.target.result, (buffer) => {
						this.watermarkAudioBuffer = buffer;
						let duration = 2.5;
						let sampleRate = 48000;
						let offlineAudioCtx = new OfflineAudioContext({
							numberOfChannels: 2,
							length: sampleRate * duration,
							sampleRate
						});

						let source = offlineAudioCtx.createBufferSource();
						source.buffer = buffer;
						source.connect(offlineAudioCtx.destination);
						source.start();

						offlineAudioCtx.startRendering().then((renderedBuffer) => res(renderedBuffer));
					});
				};
			});
			return null;
		});

		return this.watermarkAudioBufferPromise;
	}

	async initTrackSource(track, position, opts) {
		let aggregatedTrack = opts?.aggregatedTrack || this.tracks.filter(f => f.id === track.id)[0]?.__aggregatedTrack;

		if ((this.isPlaying || opts?.forceExisting) && this.trackSourcesData[track?.id] && !opts?.preventExisting) {
			return this.trackSourcesData[track.id];
		}

		let audioContext = (opts && opts.audioContext || this.audioContext);
		let source = audioContext.createBufferSource();

		let ctxTime = opts?.contextTime || audioContext.currentTime;

		let destination = opts?.masterTrackEffects?.input || audioContext.destination;

		if (__DEBUG__)
			console.debug('Init track source'/*, { trackId: track?.id, aggregatedTrack }*/);

		let trackEffects = null;

		let customEffects = (src) => {
			if (opts?.customEffects?.input) {
				src.connect(opts?.customEffects?.input);
			}
			return opts?.customEffects?.output || src;
		}

		if (opts?.buffer) {
			source.buffer = opts.buffer;
			customEffects(source).connect(destination);
		} else {
			source.buffer = opts && opts.trackBuffers && opts.trackBuffers[track.id] || this.tracksBuffers[track.id];

			if (!source.buffer) return {};
		}

		let start = track?.start - (position || 0);
		let startPos = start > 0 ? start : 0;
		let offsetPos = start < 0 ? Math.abs(start) : 0;

		let sourceData = {
			source,
			startPos,
			offsetPos,
			effects: null,
			trim: {
				start: track && track.startOffset ? track.startOffset / 1 : 0,
				end: track && track.endOffset ? track.endOffset / 1 : 0
			},
			duration: source.buffer.duration,
			position,
			contextStart: start,
			aggregatedTrack
		};

		let playDuration = Math.max(0, (sourceData.duration - sourceData.trim.start - sourceData.trim.end) - sourceData.offsetPos);
		sourceData.playDuration = playDuration;

		let playbackRate = 1; //(track?.playbackRate || 1); // is processing on pre-buffer-processing

		let realDuration = Math.max(0, (sourceData.duration - sourceData.trim.start - sourceData.trim.end) / playbackRate - sourceData.offsetPos);
		sourceData.realDuration = realDuration;
		sourceData.playbackRate = playbackRate;
		sourceData.trimmedDuration = (sourceData.duration - sourceData.trim.start - sourceData.trim.end);
		sourceData.localDuration = Math.max(sourceData.trimmedDuration - sourceData.offsetPos * playbackRate);

		if (opts?.endTime) {
			let clipEnd = track.start + track.trimmedDuration;
			if (opts?.endTime < clipEnd) {
				sourceData.localDuration = Math.max(0, sourceData.localDuration - (clipEnd - opts.endTime));
			}
		}

		/* Main pipeline */
		if (!opts?.buffer) {
			if (!opts?.noEffects) {
				sourceData.effects = opts && opts.trackEffects;
				if (!sourceData.effects)
					sourceData.effects = this.tracksEffects[aggregatedTrack?.trackNumber];
				if (!sourceData.effects && this.trackEffectsPromises[aggregatedTrack?.trackNumber])
					sourceData.effects = await this.trackEffectsPromises[aggregatedTrack?.trackNumber];
				if (!sourceData.effects && aggregatedTrack) {
					if (__DEBUG__)
						console.debug('Check: init track source');
					this.trackEffectsPromises[aggregatedTrack?.trackNumber] = this.getPipelineEffects(aggregatedTrack, audioContext, opts?.wapiPlugins);
					sourceData.effects = await this.trackEffectsPromises[aggregatedTrack?.trackNumber];
					await this.restoreTrackEffects(aggregatedTrack, sourceData.effects.filters);
					if (__DEBUG__)
						console.debug('Create effects'/*, { opts, track, audioContext, destination, effects: sourceData.effects }*/);
					this.tracksEffects[aggregatedTrack.trackNumber] = sourceData.effects;
					delete this.trackEffectsPromises[aggregatedTrack?.trackNumber];
				} else {
					if (__DEBUG__)
						console.debug('Effects exists'/*, { opts, track }*/);
				}
			}

			trackEffects = await this.getPipelineInsertionEffects(track, audioContext, sourceData, track?.insertion?.JsonConfig, aggregatedTrack);

			if (sourceData.effects) {
				source.connect(trackEffects.input);
				trackEffects.output.connect(sourceData.effects.input);
				if (__DEBUG__)
					console.debug('Connect effects'/*, { source, effects: sourceData.effects, destination }*/);
				customEffects(sourceData.effects.output).connect(destination);
				if (__DEBUG__)
					console.debug('Effects connected');
			} else {
				source.connect(trackEffects.input);
				customEffects(trackEffects.output).connect(destination);
			}
		}

		/* Clicks reduction */
		let delay = 0.02;
		let clicksNode = trackEffects?.filters?.clicksGain;
		if (clicksNode) {
			let clicksTime = [
				ctxTime + startPos,
				ctxTime + startPos + delay,
				Math.max(0, ctxTime + startPos + realDuration - delay),
				ctxTime + startPos + realDuration
			];
			clicksNode.gain.setValueAtTime(0.0001, clicksTime[0], 0);
			clicksNode.gain.exponentialRampToValueAtTime(1, clicksTime[1]);
			clicksNode.gain.setValueAtTime(1, clicksTime[2], 0);
			clicksNode.gain.exponentialRampToValueAtTime(0.0001, clicksTime[3]);
			if (__DEBUG__)
				console.debug(`Clicks gain node ${aggregatedTrack?.name} ${track?.id}`/*, { clicksTime, track, position, opts, sourceData, ctxTime: ctxTime }*/);
		}

		/* Overlapped tracks (using same clicks node for volume reduction) */
		if (clicksNode) {
			const overlappedByTracks = this.getOverlappedByTracks(track?.id).filter(f => f.insertion?.JsonConfig?.overlappingMode !== 'sourceAudible' && (!opts?.tracks?.length || opts?.tracks?.some(t => t.id === f.id)));
			if (overlappedByTracks?.length) {
				overlappedByTracks.map(ot => {
					let endOverlappedTime = ctxTime + ot.start + ot.getTrimmedDuration() + startPos - position;
					if (endOverlappedTime > 0) {
						let startOverlappedTime = Math.max(ctxTime + ot.start - position, 0);
						clicksNode.gain.setValueAtTime(1, startOverlappedTime);
						clicksNode.gain.exponentialRampToValueAtTime(0.0001, startOverlappedTime + delay + 0.01);
						clicksNode.gain.exponentialRampToValueAtTime(0.0001, endOverlappedTime - delay - 0.01);
						clicksNode.gain.exponentialRampToValueAtTime(1, endOverlappedTime);
					}
				});
			}
		}

		this.trackSourcesData[track.id] = sourceData;

		if (__DEBUG__)
			console.debug('Play source initialized'/*, { sourceData, track, position, opts, aggregatedTrack }*/);

		return sourceData;
	}

	async initMasterTrack(context) {
		if (__DEBUG__)
			console.debug('Init master track');
		const audioContext = context || this.audioContext;
		this.masterTrackEffects = await this.getPipelineEffects(this.masterTrackConfig, audioContext);
		await this.restoreTrackEffects(this.masterTrackConfig, this.masterTrackEffects.filters);
		this.masterTrackEffects.output.connect(audioContext.destination);

		const listeners = [];
		this.masterTrackSource = {
			source: {
				start: () => {
					return this.play(0);
				},
				stop: () => {
					return this.stop();
				},
				onended: null,
				addEventListener: (name, callback) => {
					listeners.push({ name, callback });
				},
				listeners
			},
			effects: this.masterTrackEffects
		};

		return this.masterTrackSource;
	}

	getTrackCanPlayed(trackId) {
		let track = this.tracks.filter(f => f.id === trackId)[0];
		if (track?.allowPublishing != null) {
			return track.allowPublishing;
		}
		if (track?.clipMuted) return false;
		let soloAggregatedTrack = this.aggregatedTracks.filter(f => f.trackNumber === this.soloTrackId)[0];
		let soloSubTracks = null;
		let soloSubTracksParentIds = null;
		let soloSubTracksAggregatedTrackNumber = null;
		if (this.soloSubTrackIds?.length) {
			soloSubTracks = this.tracks.filter(f => this.soloSubTrackIds.includes(f.id));
			soloSubTracksAggregatedTrackNumber = soloSubTracks[0]?.__parentTrack.trackNumber();

			//Parent are all tracks from aggregated track
			soloSubTracksParentIds = soloSubTracks[0]?.__aggregatedTrack?.__tracks.map(f => f.id);
		}

		if (!track) return true;

		if (!soloSubTracks?.length && soloAggregatedTrack && track.__aggregatedTrack === soloAggregatedTrack) {
			if (track.__isSubTrack) {
				if (track.__parentTrack.subTracks.some(f => !f.__internallyMuted)) {
					return !track.__internallyMuted;
				}
			} else {
				return !track.subTracks.some(f => !f.__internallyMuted);
			}
		}

		if (soloSubTracks?.length && track.__aggregatedTrack.trackNumber === soloSubTracksAggregatedTrackNumber) {
			if (!soloAggregatedTrack || soloAggregatedTrack.trackNumber === track.__aggregatedTrack?.trackNumber) {
				return this.soloSubTrackIds.includes(track.id) || (!track.__isSubTrack && !soloSubTracksParentIds.includes(track.id));
			}
		}
		if (soloAggregatedTrack) {
			return !track.__isSubTrack && track.__aggregatedTrack.trackNumber === soloAggregatedTrack.trackNumber;
		} else {
			return !(track?.__aggregatedTrack?.muted || track?.__aggregatedTrack?.__internallyMuted) && !track?.__internallyMuted;
		}
	}

	async play(position, opts) {
		if (this.isBusy) return;

		this.states.initPlaying = true;
		this.setIsBusy(true);
		try {
			opts = _.merge({
				trackBuffers: {},
				tracksEffects: {},
				duration: null,
				tracks: [],
				solo: null,
				onStop: null
			}, opts || {});

			if (position && this.getTotalDuration() > 0 && parseInt(this.getTotalDuration() * 10) <= parseInt(position * 10)) {
				position = 0;
			}

			if (__DEBUG__)
				console.debug('Play', position, { opts });

			await this.stop(true);

			let audioContext = (opts && opts.audioContext || this.audioContext);

			let onStopProcessed = false;
			const onStop = () => {
				if (opts?.onStop && !onStopProcessed) {
					if (opts.onStop)
						opts.onStop();
					onStopProcessed = true;
				}
			}

			await audioContext.suspend();

			await this.initMasterTrack(audioContext, opts);

			if (this.initOpts?.onMasterTrackInit) {
				this.initOpts?.onMasterTrackInit(this.masterTrackSource);
			}

			let nextPlayingSources = null;
			let iteration = 0;
			let duration = opts?.endTime - position;
			
			this.$prepareSources = (isNextSources) => {
				let playSessionId = uuid();
				
				let ctxTime = audioContext.currentTime + (iteration * duration || 0);
				iteration++;

				return Promise.all(([...(opts.solo ? [] : this.tracks), ...opts.tracks]).map(async track => {
					if (this.getTrackCanPlayed(track.id) || opts?.forcePlayTracks?.includes(track.id)) {
						if (this.trackSources[track.id] && !isNextSources) {
							try {
								this.trackSources[track.id].stop();
								this.trackSources[track.id].disconnect();
								delete this.trackSources[track.id];
							} catch {
								//
							}
						}
						
						let sourceData = await this.initTrackSource(track, position, { ...(opts || {}), audioContext, masterTrackEffects: this.masterTrackEffects, preventExisting: isNextSources, contextTime: ctxTime });
						let source = sourceData.source;
						let startPos = sourceData.startPos;
						let offsetStartPos = sourceData.offsetPos;

						if (!source) return;

						if (sourceData.localDuration > 0) {
							//offset and duration properties should be specified in source length (without playback rate scaling that actuall applied to audio)
							sourceData.play = (time) => {
								time = time || ctxTime;
								this.playSessionId = playSessionId;
								this.trackSources[track.id] = source;
								this.trackSourcesState[track.id] = 1;
								source.start(time + startPos, Math.max(offsetStartPos * (sourceData.playbackRate || 1) + sourceData.trim.start, 0), sourceData.localDuration);

								if (__DEBUG__)
									console.debug('Start play source', { time: time + startPos, startPos, dur: sourceData.localDuration, offset: Math.max(offsetStartPos * (sourceData.playbackRate || 1) + sourceData.trim.start, 0) });
							}
							
							let scopePlaySessionId = playSessionId;
							source.addEventListener('ended', () => {
								if (scopePlaySessionId === playSessionId) {
									if (this.trackSourcesState[track.id] === 1)
										this.trackSourcesState[track.id] = 2;
									if (this.isPlaying && Object.keys(this.trackSourcesState).length && !this.tracks.filter(f => this.trackSourcesState[f.id] === 1).length) {
										this.currentTime = audioContext.currentTime - this.contextPlayingStartTime;
										this.listeners.filter(f => f.type === 'ended').map(f => f.listener(this));
										if (__DEBUG__)
											console.debug('Stop play by state', { opts });

										if (!opts?.repeat || !nextPlayingSources?.length) {
											this.stop().then(() => {
												this.trackSourcesState = {};
												onStop();
											});
										} else {
											const playingSources = nextPlayingSources.filter(f => f);
											this.processSidechain(playingSources);
											playingSources.map(f => f?.play && f.play(audioContext.currentTime));
											Object.keys(this.trackSourcesState).map(f => this.trackSourcesState[f] = 1);

											this.$prepareSources(true).then((r) => {
												nextPlayingSources = r;
											});

											if (__DEBUG__)
												console.debug('Repeat');
										}
									}
								}
							});
						}

						return sourceData;
					}
				}))
			};

			const sources = await this.$prepareSources();

			const playingSources = sources.filter(f => f);
			this.processSidechain(playingSources);
			playingSources.map(f => f?.play && f.play());

			if (opts.duration && !opts?.repeat) {
				let scopePlaySessionId = this.playSessionId;
				setTimeout(() => {
					if (this._isDestroyed) return;
					if (scopePlaySessionId === this.playSessionId) {
						this.stop(true).then(() => {
							onStop();
						});
					}
				}, opts.duration * 1000);
			}

			if (opts?.repeat) {
				this.$prepareSources(true).then((r) => {
					nextPlayingSources = r;
				});
			}

			await this.checkWatermark(audioContext);

			audioContext.resume();

			this.isPlaying = true;
			this.contextPlayingStartTime = audioContext.currentTime - position;

			if (this.opts?.onPlay) {
				this.opts?.onPlay();
			}

			this.listeners.filter(f => f.type === 'play').map(f => f.listener(this));
		} finally {
			this.states.initPlaying = false;
			this.setIsBusy(false);
		}
	}

	processSidechain(playingSources) {
		playingSources.map(f => {
			const sidechainPlugins = f.effects?.filters?.wapiNodes?.filter(p => p?.supportSidechain) || [];
			sidechainPlugins.map(p => {
				const sidechainTracks = p?.trackConfig?.sidechainTrackNumbers || [];
				if (p?.instance?.initSidechain && sidechainTracks?.length) {
					const affectingSources = playingSources.filter(f => sidechainTracks.includes(f?.aggregatedTrack?.trackNumber));
					if (affectingSources.length) {
						p.instance.initSidechain(affectingSources.map(s => s.effects.output));
						if (__DEBUG__)
							console.debug('Sidechain'/*, { p, affectingSources }*/);
					}
				}
			});
		})
	}

	async stop() {
		if (!this.audioContext) return;

		if (this.states.isStopping) return;

		this.states.isStopping = true;
		this.setIsBusy(true);
		try {
			if (__DEBUG__)
				console.debug('Stop play');

			if (this.isPlaying) {
				this.listeners.filter(f => f.type === 'stop').map(f => f.listener(this));
			}

			this.trackSourcesState = {};
			for (let trackId in this.trackSources) {
				try {
					if (this.trackSources[trackId])
						this.trackSources[trackId].stop();
				} catch {
					//
				}
				this.trackSources[trackId] = null;
				this.stopFilters(this.trackSourcesData[trackId]?.effects?.filters);
				this.stopFilters(this.masterTrackEffects?.filters);
			}

			if (this.watermarkPlayingSource) {
				try {
					this.watermarkPlayingSource?.stop();
				} catch {
					//
				}
			}
			
			if (this.audioContext.state !== 'closed') {
				await this.audioContext.suspend();
			}

			if (this.opts?.onStop && this.isPlaying) {
				this.opts?.onStop();
			}

			this.isPlaying = false;
		} finally {
			this.states.isStopping = false;
			this.setIsBusy(false);
		}
	}

	stopFilters(filters) {
		if (!filters) return;
		for (let filter in (filters || {})) {
			if (filters[filter]?.disconnect) {
				filters[filter].disconnect();
			}
		}
	}

	bufferToWave(abuffer, len) {
		return audioHelper.bufferToWave(abuffer, len);
	}

	downloadFinalAudio(opts) {
		return this.generateFinalAudio(opts).then(r => {
			return this.downloadBlob(r.wav, `${this.finalAudioName}.wav`);
		})
	}

	downloadBlob(blob, name) {
		let new_file = URL.createObjectURL(blob);

		let link = document.createElement('a');
		link.href = new_file;
		link.download = name;
		link.click();

		return new_file;
	}

	async generateFinalAudio(opts) {
		await this.stop();

		opts = _.assign({
			sampleRate: 48000,
			silenceStart: 0,
			silenceEnd: 0,
			type: 'audio/wav',
			singleProcessedTrackId: null,
			duration: 0,
			startPosDiff: 0
		}, opts || {});

		if (__DEBUG__)
			console.debug('Generate final audio'/*, opts*/);

		let startPosDiff = opts.startPosDiff || 0;

		let offlineAudioCtx = new OfflineAudioContext({
			numberOfChannels: 2,
			length: opts.sampleRate * (opts.duration || this.getTotalDuration()),
			sampleRate: opts.sampleRate
		});

		let masterTrackEffects = null;
		if (opts.singleProcessedTrackId === null) {
			masterTrackEffects = await this.getPipelineEffects(this.masterTrackConfig, offlineAudioCtx);
			await this.restoreTrackEffects(this.masterTrackConfig, masterTrackEffects.filters);
			masterTrackEffects.output.connect(offlineAudioCtx.destination);
		}

		return new Promise((resolve, reject) => {
			let mixedItems = 0;
			return Promise.all(this.aggregatedTracks.map(async aggTrack => {
				if (opts.singleProcessedTrackId !== null) {
					if (opts.singleProcessedTrackId === aggTrack.trackNumber) {
						let tracks = this.getAggregatedTrackItems(aggTrack.trackNumber, true);
						if (__DEBUG__)
							console.debug('Publish tracks'/*, { tracks, canBePlayed: tracks.filter(f => this.getTrackCanPlayed(f.id)) }*/);
						await Promise.all(tracks.filter(f => this.getTrackCanPlayed(f.id)).map(async f => {
							let sourceData = await this.initTrackSource({ ...f, start: f.start - startPosDiff }, 0, { audioContext: offlineAudioCtx, noEffects: true, trackBuffers: opts?.trackBuffers, tracks: opts?.tracks });
							let source = sourceData.source;
							let startPos = sourceData.startPos;
							let offsetStartPos = sourceData.offsetPos;

							if (!source) return;
							let playDuration = Math.max(0, sourceData.playDuration);
							if (__DEBUG__)
								console.debug(`SingleTrackProcessedResult (building for ${aggTrack.name})`/*, { sourceData, startPosDiff, playDuration }*/);
							source.start(startPos, Math.max(offsetStartPos + sourceData.trim.start, 0), playDuration);
							mixedItems++;
						}));
					}
				} else {
					let aggTracks = this.getAggregatedTrackItems(aggTrack.trackNumber, true);
					let maxDuration = _.max(aggTracks.map(f => f.start + f.getTrimmedDuration()));
					let trackStartPos = _.min(aggTracks.map(f => f.start));
					let singleTrackProcessedResult = await this.generateFinalAudio({ ...opts, singleProcessedTrackId: aggTrack.trackNumber, duration: maxDuration - trackStartPos, startPosDiff: trackStartPos });
					let buffer = singleTrackProcessedResult?.buffer;
					if (__DEBUG__)
						console.debug('SingleTrackProcessedResult'/*, { singleTrackProcessedResult, maxDuration, trackStartPos, bufferDuration: buffer?.duration, ctxLength: offlineAudioCtx.length }*/);
					if (aggTrack.postEffects?.plugins?.length && singleTrackProcessedResult?.mixedItems) {
						let pluginsProcessedData = await postEffects.processPlugins({ audioBuffer: buffer }, aggTrack.postEffects, offlineAudioCtx);
						buffer = pluginsProcessedData?.buffer;
					}

					if (!buffer) return;

					if (__DEBUG__)
						console.debug('Check: ', { start: trackStartPos, duration: buffer.duration });
					let sourceData = await this.initTrackSource({ id: 'mixedTrack', start: trackStartPos, duration: buffer.duration }, 0, { trackBuffers: { 'mixedTrack': buffer }, audioContext: offlineAudioCtx, masterTrackEffects, aggregatedTrack: aggTrack });
					let source = sourceData.source;

					if (!source) return;
					sourceData.play = () => source.start(trackStartPos, 0, buffer.duration);
					mixedItems++;

					return sourceData;
				}
			})).then(async (tracks) => {
				if (opts.singleProcessedTrackId === null) {
					await this.checkWatermark(offlineAudioCtx);
					const playingTracks = tracks.filter(f => f);
					this.processSidechain(playingTracks.filter(f => f));
					playingTracks.map(f => f.play && f.play());
				}

				offlineAudioCtx.startRendering().then(async (renderedBuffer) => {
					if (mixedItems) {
						let pluginsProcessedData = await postEffects.processPlugins({ audioBuffer: renderedBuffer }, opts.singleProcessedTrackId === null ? this.masterTrackConfig?.postEffects : [], offlineAudioCtx);
						renderedBuffer = pluginsProcessedData?.buffer || renderedBuffer;
						let wav = null;
						if (renderedBuffer) {
							wav = this.bufferToWave(renderedBuffer, renderedBuffer.length);
						}

						let result = {
							buffer: renderedBuffer,
							length: renderedBuffer?.length,
							wav,
							mixedItems
						};

						if (__DEBUG__)
							console.debug('Audio rendered'/*, { renderedBuffer, result }*/);

						resolve(result);
					} else {
						resolve({
							buffer: null,
							length: 0,
							wav: null,
							mixedItems
						});
					}
				}).catch((err) => {
					reject(err);
				});
			}).catch(ex => reject(ex));
		});
	}

	async previewPlugins(opts) {
		await this.stopPreviewPlugins();

		const playId = uuid();
		this.$previewPluginRunId = playId;

		if (opts) {
			const aggTrack = opts?.aggregatedTrack;
			const maxDuration = _.max(aggTrack.__tracks.map(v => v.duration + v.start));
			const trackStartPos = _.min(aggTrack.__tracks.map(f => f.start));
			const singleTrackProcessedResult = await this.generateFinalAudio({ ...opts, singleProcessedTrackId: opts.aggregatedTrack?.trackNumber, duration: maxDuration - trackStartPos, startPosDiff: trackStartPos });
			let buffer = singleTrackProcessedResult?.buffer;
			if (aggTrack.postEffects?.plugins?.length && singleTrackProcessedResult?.mixedItems) {
				let pluginsProcessedData = await postEffects.processPlugins({ audioBuffer: buffer }, aggTrack.postEffects, this.audioContext);
				buffer = pluginsProcessedData?.buffer;
			}
			if (__DEBUG__)
				console.debug('onPreviewPlugins'/*, { singleTrackProcessedResult, maxDuration, trackStartPos, bufferDuration: buffer?.duration }*/);

			if (playId !== this.$previewPluginRunId) return;
			let trackSource = await this.initTrackSource({}, 0, { buffer });
			this.$previewPluginsSource = trackSource?.source;
			if (this.$previewPluginsSource) {
				this.$previewPluginsSource.start();
			}
			if (trackSource?.source) {
				if (opts?.loaded)
					opts?.loaded();
				trackSource.source.addEventListener('ended', () => {
					if (opts?.ended)
						opts?.ended();
				});
			} else {
				if (opts?.ended)
					opts?.ended();
			}
		}
	}

	stopPreviewPlugins() {
		if (this.$previewPluginsSource) {
			try {
				this.$previewPluginsSource.stop();
			} catch {
				//
			}
		}
		delete this.$previewPluginRunId;
		delete this.$previewPluginsSource;
	}

	/* Pipeline */

	async getPipelineEffects(aggregatedTrack, customAudioContext, runtimeWapiPlugins) {
		const audioContext = customAudioContext || this.audioContext;

		var input = null;
		var output = null;

		var doConnect = (inNode, outNode) => {
			if (!input)
				input = inNode || outNode;
			if (output && inNode)
				output.connect(inNode);
			output = outNode || inNode || output;
		};

		const gainNode = audioContext.createGain();
		gainNode.gain.value = 1;
		doConnect(gainNode);

		let panNode = audioContext.createStereoPanner();
		doConnect(panNode);
		panNode.pan.value = 0;

		var wapiNodes = [];
		var wapiPromises = aggregatedTrack?.wapiPlugins?.filter(f => f.enabled && f.data).map(async f => {
			var plugin = runtimeWapiPlugins?.filter(f => f.id === f.id)[0];
			if (!plugin) {
				plugin = await wapiPluginsList.getPlugin(f.id, { audioContext });
			}
			if (plugin?.instance) {
				var nodes = await plugin?.pipeline;
				if (nodes) {
					doConnect(nodes.input, nodes.output);
					plugin.nodes = nodes;
					plugin.trackConfig = f;
					await plugin?.instance.setValues(f.data);
					wapiNodes.push(plugin);
				}
			}
		});
		if (wapiPromises?.length)
			await Promise.all(wapiPromises);

		var sampleSize = 512;

		const analyserNode = audioContext.createAnalyser();
		analyserNode.fftSize = sampleSize;
		output.connect(analyserNode);

		const stereoAnalyser = audioHelper.StereoAnalyserNode(audioContext);
		stereoAnalyser.fftSize = sampleSize;
		output.connect(stereoAnalyser);

		return {
			input,
			output,
			filters: {
				gain: gainNode,
				analyzer: analyserNode,
				stereoAnalyser,
				panner: panNode,
				wapiNodes
			}
		};
	}

	async restoreTrackEffects(track, effects) {
		if (!effects || !track) return;

		if (effects.filters)
			effects = effects.filters;

		//restore volume
		if (track.volume != null)
			effects.gain.gain.value = track.volume;

		//restore panner
		if (track.panner)
			effects.panner.pan.value = track.panner || 0;

		//restore wapi plugins
		if (effects?.wapiNodes?.length) {
			var effectsPromises = track?.wapiPlugins?.filter(f => f.enabled && f.data).map(async f => {
				var plugin = effects.wapiNodes.find(n => n.id === f.id);
				if (!plugin) return;
				if (plugin?.instance) {
					await plugin?.instance.setValues(f.data);
				}
			});
			if (effectsPromises?.length)
				await Promise.all(effectsPromises);
		}

		if (__DEBUG__)
			console.debug('Restore track effects'/*, { effects, track }*/);
	}

	async getPipelineInsertionEffects(track, customAudioContext, sourceData, trackJsonConfig, aggregatedTrack) {
		if (__DEBUG__)
			console.debug('getPipelineInsertionEffects', { track, customAudioContext, sourceData, trackJsonConfig });

		const audioContext = customAudioContext || this.audioContext;

		const clicksGainNode = audioContext.createGain();
		clicksGainNode.gain.value = 1;

		var input = null;
		var output = null;

		var doConnect = (node) => {
			if (!input) {
				input = node;
			} else {
				output.connect(node);
			}
			output = node;
		};

		//Fade in/out
		var invokeFade = (node, type, value, start, end) => {
			if (__DEBUG__)
				console.debug('Invoke fade'/*, { node, type, value, start, end, sourceData }*/);
			switch (type) {
				case 'linear':
					node.gain.linearRampToValueAtTime(value, end);
					break;
				case 'exponential':
					var curve = createLogarithmicBuffer(1, audioContext.samplerate);
					if (__DEBUG__)
						console.log('Fade curve'/*, { curve }*/);
					node.gain.setValueCurveAtTime(value > 0.5 ? curve : curve.reverse(), start, end - start);
					break;
			}
		}
		let fadeInNode = null;
		if (trackJsonConfig?.fadeIn?.value) {
			if (__DEBUG__)
				console.debug('Fade in'/*, { sourceData }*/);
			if (sourceData?.contextStart >= 0) {
				fadeInNode = audioContext.createGain();
				fadeInNode.gain.setValueAtTime(0.0001, sourceData?.contextStart);
				invokeFade(fadeInNode, trackJsonConfig?.fadeIn?.type, 1, sourceData?.contextStart + 0.0001, trackJsonConfig?.fadeIn?.value + sourceData?.contextStart);
				doConnect(fadeInNode);
			}
		}
		let fadeOutNode = null;
		if (trackJsonConfig?.fadeOut?.value) {
			let fadeOutStart = sourceData?.startPos + sourceData?.realDuration - trackJsonConfig?.fadeOut?.value;
			if (__DEBUG__)
				console.debug('Fade out'/*, { sourceData, fadeOutStart }*/);
			if (fadeOutStart >= 0) {
				fadeOutNode = audioContext.createGain();
				fadeOutNode.gain.setValueAtTime(1, fadeOutStart);
				invokeFade(fadeOutNode, trackJsonConfig?.fadeOut?.type, 0.0001, fadeOutStart + 0.0001, fadeOutStart + trackJsonConfig?.fadeOut?.value);
				doConnect(fadeOutNode);
			}
		}

		//Envelopes common functions
		const lerp = (x, y, a) => x * (1 - a) + y * a;
		let getEnvelopePoints = (points, defValue) => {
			let result = [];
			let playingTime = (sourceData?.duration - sourceData?.localDuration) || 0;
			result.push(...points.filter(f => f.time >= playingTime).map(f => ({
				time: f.time - playingTime,
				value: f.value
			})));

			if (playingTime > 0) {
				let itemBefore = points.filter(f => f.time < playingTime).slice(-1)[0] || {
					time: 0,
					value: defValue || 1
				};
				let itemAfter = points.filter(f => f.time >= playingTime)[0] || {
					time: sourceData?.duration,
					value: defValue || 1
				};
				result.splice(0, 0, {
					time: 0,
					value: lerp(itemBefore.value, itemAfter.value, (playingTime - itemBefore.time) / (itemAfter.time - itemBefore.time))
				});

				if (__DEBUG__)
					console.debug('Calculate envelope points', { result, itemAfter, itemBefore, playingTime });
			}

			return result;
		}

		//Volume envelope
		let volumeEnvelopeNode = null;
		if (trackJsonConfig?.volumeEnvelope?.enabled && sourceData?.localDuration > 0) {
			let points = getEnvelopePoints(trackJsonConfig?.volumeEnvelope?.points);
			volumeEnvelopeNode = audioContext.createGain();
			let startPoint = points.filter(f => f.time === 0)[0] || { time: 0, value: 1 };
			let endPoint = points.filter(f => f.time >= track?.trimmedDuration)[0] || { time: track?.trimmedDuration, value: 1 };
			let lastTime = startPoint.time;

			let addValueTime = (value, time) => {
				if (time - lastTime < 0.5) {
					volumeEnvelopeNode.gain.exponentialRampToValueAtTime(value || 0.0001, time + Math.max(0, sourceData?.contextStart));
				} else {
					volumeEnvelopeNode.gain.linearRampToValueAtTime(value || 0.0001, time + Math.max(0, sourceData?.contextStart));
				}
			};

			volumeEnvelopeNode.gain.setValueAtTime(startPoint.value, startPoint.time + Math.max(0, sourceData?.contextStart));
			points.map(f => {
				if (f.time !== startPoint.time && f.time !== endPoint.time) {
					addValueTime(f.value, f.time);
				}
				lastTime = f.time;
			});
			addValueTime(endPoint.value, endPoint.time);
			doConnect(volumeEnvelopeNode);

			if (__DEBUG__)
				console.debug('Volume envelope'/*, { envelope: trackJsonConfig?.volumeEnvelope, startPoint, endPoint }*/);
		}

		//Pitch envelope
		let pitchEnvelopeNode = null;
		if (trackJsonConfig?.pitchEnvelope?.enabled && sourceData?.localDuration > 0) {
			pitchEnvelopeNode = await initSoundTouchNode(audioContext);
			let pitchFactorParam = pitchEnvelopeNode.parameters.get('pitch');

			let points = getEnvelopePoints(trackJsonConfig?.pitchEnvelope?.points);
			let startPoint = points.filter(f => f.time === 0)[0] || { time: 0, value: 1 };
			let endPoint = points.filter(f => f.time >= track?.trimmedDuration)[0] || { time: track?.trimmedDuration, value: 1 };
			let lastTime = startPoint.time;

			let addValueTime = (value, time) => {
				if (time - lastTime < 0.5) {
					pitchFactorParam.exponentialRampToValueAtTime(value || 0.0001, time + Math.max(0, sourceData?.contextStart));
				} else {
					pitchFactorParam.linearRampToValueAtTime(value || 0.0001, time + Math.max(0, sourceData?.contextStart));
				}
			};

			pitchFactorParam.setValueAtTime(startPoint.value, startPoint.time + Math.max(0, sourceData?.contextStart));
			points.map(f => {
				if (f.time !== startPoint.time && f.time !== endPoint.time) {
					addValueTime(f.value, f.time);
				}
				lastTime = f.time;
			});
			addValueTime(endPoint.value, endPoint.time);
			
			if (__DEBUG__)
				console.debug('Pitch envelope');

			doConnect(pitchEnvelopeNode);
		}

		//Normalization
		let normalizeGain = null;
		if (aggregatedTrack?.normalize?.enabled && track?.__audioInfo) {
			const methodValue = aggregatedTrack?.normalize?.method === 'lufs' ? track?.__audioInfo?.momentaryMaxLUFS : track?.__audioInfo?.peakDb;
			const gain = audioHelper.dbToGain(aggregatedTrack?.normalize?.value - methodValue);
			if (__DEBUG__)
				console.debug('Normalization', { gain, methodValue, normalizeValue: aggregatedTrack?.normalize?.value });

			normalizeGain = audioContext.createGain();
			if (gain >= normalizeGain.gain.minValue && gain <= normalizeGain.gain.maxValue) {
				normalizeGain.gain.value = gain;
				doConnect(normalizeGain);
			}
		}

		//Clip volume
		let clipVolume = track?.volumeRate || 1;
		if (clipVolume !== 1) {
			let clipVolumeGain = audioContext.createGain();
			clipVolumeGain.gain.value = clipVolume;
			doConnect(clipVolumeGain);
		}

		doConnect(clicksGainNode);

		//Mute
		if (track?.clipMuted) {
			let clipMuteGain = audioContext.createGain();
			clipMuteGain.gain.value = 0;
			doConnect(clipMuteGain);
		}

		return {
			input,
			output,
			filters: {
				clicksGain: clicksGainNode,
				fadeIn: fadeInNode,
				fadeOut: fadeOutNode,
				volumeEnvelope: volumeEnvelopeNode,
				pitchEnvelope: pitchEnvelopeNode,
				normalizeGain
			}
		};
	}

	createGainNode(ctx) {
		const gainNode = ctx.createGain();
		gainNode.gain.value = 1;

		return gainNode;
	}

	createEqBandFilter(opts, audioContext) {
		var filter = audioContext.createBiquadFilter();

		filter.type = opts?.type || 'peaking';
		filter.frequency.value = opts?.frequency;
		filter.Q.value = opts?.q || 1;
		filter.gain.value = opts?.gain || 0;

		return filter;
	}

	init5BandEqualizer(audioContext) {
		var frequencies = [60, 250, 1000, 4000, 16000];
		var filters = frequencies.map(v => this.createEqBandFilter({ frequency: v }, audioContext));

		filters.reduce((prev, curr) => {
			prev.connect(curr);
			return curr;
		});

		return filters;
	}

	initDynamicEqualizer(audioContext, trackEqValues) {
		if (!trackEqValues?.length) return [];
		var filters = trackEqValues.map(v => this.createEqBandFilter(v, audioContext));

		filters.reduce((prev, curr) => {
			prev.connect(curr);
			return curr;
		});

		return filters;
	}

	setEqualizerFromArrayValues(bands, data) {
		(data || []).map((val, i) => {
			if (bands[i])
				bands[i].gain.value = parseFloat(val) || 0;
		});
		return bands.map(eq => eq.gain.value);
	}

	/* Tracker */

	getOverlappedByTracks(trackId) {
		const track = this.tracks.filter(f => f.id === trackId)[0];
		if (!track || track.__isSubTrack) return [];
		const trackStart = track.start;
		const trackEnd = track.start + track.getTrimmedDuration();
		return this.tracks.filter(t => t.trackNumber() === track?.trackNumber() && !t.__isSubTrack && t.id !== track?.id &&
			t.start < trackEnd && (t.start + t.getTrimmedDuration()) > trackStart && t.start > trackStart);
	}

	getTotalDuration() {
		const visibleTracks = this.tracks.filter(f => f.__isVisible && (!auth.isPublishMode() || f.__allowPublishing !== false));
		
		var max = 0;
		visibleTracks.map(f => {
			max = Math.max(max, (f.start || 0) + (f.getTrimmedDuration() || 0));
		});
		return max;
	}

	getAggregatedTrackItems(aggregatedTrack, all) {
		return this.tracks.filter(f => f.trackNumber() === aggregatedTrack && (all || !f.__isSubTrack));
	}
}

export {
	AudioSystem
}
